// Nuevos tipos para el sistema de minutas - VERSIÓN DINÁMICA DESDE BD
// Interface unificada que coincide con la tabla tipos_plato de la BD

// ============================================================================
// TIPOS DE PLATOS DINÁMICOS DESDE BD
// ============================================================================

export interface TipoPlato {
  id: number; // INTEGER autoincrement desde BD
  nombre: string;
  descripcion?: string | null;
  orden?: number | null;
  color?: string | null;
  activo?: boolean | null;
  created_at?: Date | null;
  updated_at?: Date | null;
}

// DEPRECATED: Usar TipoPlato en su lugar
export interface TipoPlatoMinuta {
  id: string;
  nombre: string;
  descripcion?: string;
  orden: number;
  color: string;
  activo: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// NOTA: CONSTANTE TIPOS_PLATOS_MINUTAS ELIMINADA
// ============================================================================
// Los tipos de platos ahora se obtienen dinámicamente desde la BD a través de la API
// Usar TiposPlato.obtenerTodos() para cargar tipos desde /api/tipos-plato
// La tabla tipos_plato en BD contiene los 10 tipos normalizados con integer IDs

// ============================================================================
// MINUTAS DETALLADAS
// ============================================================================

export interface MinutaDetallada {
  id: number;
  minutaId: number;
  diaSemana: number; // 1=Lunes, 2=Martes, ..., 7=Domingo
  fechaEspecifica: Date;
  tipoPlatoId: number;
  recetaId: number;
  porcionePlanificadas: number;
  activo: boolean;
  createdAt: Date;
  updatedAt: Date;

  // Relaciones
  minuta?: MinutaNueva;
  tipoPlato?: TipoPlatoMinuta;
  receta?: RecetaNueva;
}

// ============================================================================
// ESTADÍSTICAS
// ============================================================================

export interface EstadisticasMinuta {
  totalRecetas: number;
  diasConRecetas: number;
  tiposConRecetas: number;
  costoTotal: number;
  costPromedioDia: number; // Reemplaza pedidosAsociados
  completitudPorcentaje: number;
  celdasTotales?: number; // Total de celdas esperadas (incluye tipos con recetas aunque no estén en configuración)
}

// ============================================================================
// MINUTA NUEVA ESTRUCTURA
// ============================================================================

export interface MinutaNueva {
  id: number;
  nombre: string;
  descripcion?: string;
  fechaInicio: Date;
  fechaFin: Date;
  estado: EstadoMinuta;
  creadoPor: number;
  activo: boolean;
  createdAt: Date;
  updatedAt: Date;

  // Configuración JSON de filas por tipo de plato
  configuracionFilas?: Record<string, number>;

  // Relaciones
  creador?: Usuario;
  minutasDetalladas?: MinutaDetallada[];
  pedidos?: PedidoNuevo[];

  // Estadísticas calculadas (incluidas en la respuesta del backend)
  estadisticas?: EstadisticasMinuta;
}

// ============================================================================
// RECETA NUEVA ESTRUCTURA
// ============================================================================

export interface RecetaNueva {
  id: number;
  nombre: string;
  descripcion?: string;
  categoria: string;
  tipoComida: string; // Mantenemos por compatibilidad
  tipoPlatoNuevo?: string; // Nuevo campo para tipos específicos
  instrucciones: string;
  costoTotal: number;
  estado: EstadoReceta;
  chefId: number;
  activo: boolean;
  createdAt: Date;
  updatedAt: Date;

  // Relaciones
  chef?: Usuario;
  ingredientes?: IngredienteReceta[];
  minutasDetalladas?: MinutaDetallada[];
  tipoPlato?: TipoPlatoMinuta;
}

// ============================================================================
// PEDIDOS NUEVA ESTRUCTURA
// ============================================================================

export interface PedidoNuevo {
  id: number;
  codigo?: string;
  clienteId: number;
  minutaId: number;
  fechaEntrega: Date;
  estado: EstadoPedido;
  total: number;
  observaciones?: string;
  createdAt: Date;
  updatedAt: Date;

  // Relaciones
  cliente?: Cliente;
  minuta?: MinutaNueva;
  detallesNuevos?: PedidoDetalleNuevo[];
}

export interface PedidoDetalleNuevo {
  id: number;
  pedidoId: number;
  minutaDetalladaId: number;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
  createdAt: Date;
  updatedAt: Date;

  // Relaciones
  pedido?: PedidoNuevo;
  minutaDetallada?: MinutaDetallada;
}

// ============================================================================
// UTILIDADES Y HELPERS
// ============================================================================

// Mapeo de días de la semana
export const DIAS_SEMANA = {
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
  7: 'Domingo',
} as const;

export const DIAS_SEMANA_ABREV = {
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
  7: 'Dom',
} as const;

// Estados del sistema (mantenemos compatibilidad)
export type EstadoMinuta = 'borrador' | 'activa' | 'completada' | 'cancelada';
export type EstadoReceta = 'borrador' | 'disponible' | 'archivada';
export type EstadoPedido =
  | 'reservado'
  | 'confirmado'
  | 'en_preparacion'
  | 'listo'
  | 'despachado'
  | 'cancelado';

// Interfaces auxiliares
export interface Usuario {
  id: number;
  nombre: string;
  email: string;
  rol: string;
}

export interface Cliente {
  id: number;
  nombre: string;
  email: string;
  rut: string;
}

export interface IngredienteReceta {
  id: number;
  recetaId: number;
  ingredienteId: number;
  cantidad: number;
  unidad: string;
  costoParcial: number;
}

// ============================================================================
// VISTA DE MINUTA TIPO EXCEL
// ============================================================================

// Estructura para mostrar la minuta como tabla de 7 días x 10 tipos de platos
export interface VistaMinutaExcel {
  minuta: MinutaNueva;
  semana: {
    [diaSemana: number]: {
      [tipoPlatoId: string]: MinutaDetallada | null;
    };
  };
  estadisticas: {
    totalRecetas: number;
    recetasPorDia: { [dia: number]: number };
    recetasPorTipo: { [tipo: string]: number };
    diasCompletos: number;
    tiposCompletos: number;
  };
}

// ============================================================================
// FORMULARIOS Y REQUESTS
// ============================================================================

export interface CrearMinutaDetalladaRequest {
  minutaId: number;
  diaSemana: number;
  fechaEspecifica: string; // YYYY-MM-DD
  tipoPlatoId: number; // INTEGER desde nueva tabla tipos_plato
  recetaId: number;
  porcionePlanificadas: number;
}

export interface ActualizarMinutaDetalladaRequest {
  porcionePlanificadas?: number;
  recetaId?: number;
  activo?: boolean;
}

export interface CrearPedidoNuevoRequest {
  clienteId: number;
  minutaId: number;
  fechaEntrega: string; // YYYY-MM-DD
  observaciones?: string;
  detalles: CrearPedidoDetalleNuevoRequest[];
}

export interface CrearPedidoDetalleNuevoRequest {
  minutaDetalladaId: number;
  cantidad: number;
}

// ============================================================================
// RESPUESTAS DE API - VERSIÓN UNIFICADA DINÁMICA
// ============================================================================

export interface TiposPlatosResponse {
  success: boolean;
  data: TipoPlato[];
  total: number;
}

// DEPRECATED: Usar TiposPlatosResponse en su lugar
export interface TiposPlatosMinutasResponse {
  success: boolean;
  data: TipoPlatoMinuta[];
  total: number;
}

export interface MinutasDetalladasResponse {
  success: boolean;
  data: MinutaDetallada[];
  total: number;
}

export interface VistaMinutaExcelResponse {
  success: boolean;
  data: VistaMinutaExcel;
}

export interface MinutasNuevasResponse {
  success: boolean;
  data: MinutaNueva[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
